/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { connect, useDispatch } from "react-redux";
import { omit } from "lodash";
import { push, goBack } from "connected-react-router";
import Typography from "@mui/material/Typography";
import { bindActionCreators } from "redux";
import { Box, Link } from "@mui/material";
import { t, tl } from "../../../components/translate";
import List from "../../../components/List";
import EmployeeInfo from "../../Employee/EmployeeShow";
import Authorize from "../../../auth/authorization";
import { resourceCentreEmployeeActions } from "../../../actions";
import * as NotificationActions from "../../../actions/notification";
import { removeGeneralErrorByKey } from "../../../actions/error";
import { employeeFullNameSelector } from "../../../reducers/employee";
import { User } from "../../../interfaces/UserContext";
import { Employee, PermissionGroups, UserMode } from "../../../interfaces/User";
import { ResourceCentre } from "../../../interfaces/ResourceCentreInterface";
import { IThunkDispatch, RootState } from "../../../store";
import useCan from "../../../hooks/useCan";
import { ledgerFindBySubLedgerType } from "../../accounts/hooks";
import { SubLedgerTypes } from "../../../interfaces/Accounts";
import useIsAccountSubscribed from "../../../hooks/accounts";
import useGetDepartments from "../../../hooks/department";
import { SETTING_ROUTES } from "../ResourceCentreShow";
import { ALL_DEPARTMENT } from "./SPCreateEdit";
import { ListActions, Menu, MenuItem } from "../../../components/OList";
import CsvUploader from "../../../components/CsvUploader/CsvUploader";
import { employeeUploadColumns } from "../../../components/CsvUploader/csvHelpers";
import { batchUploadEmployee, batchUploadNonUserEmployee } from "../../../api/user";
import UserChip from "../../Modals/User/Chip";
import EmployeeCreateEdit from "./EmployeeCreateEdit";

interface AdminsProps {
  resourceCentre: ResourceCentre;
  editMode: boolean;
  createMode: boolean;
  employeeId: number;
  resourceCentreState: { selectedEmployee: number | null };
  user: User & { role: UserMode; permissionGroup: PermissionGroups };
  onRowClick: (id: number) => void;
  closeInfo: () => void;
  onClose: () => void;
  onSave: (data: Partial<Employee>, id: number, afterSave: (id: number) => void) => void;
  goBack: () => void;
  navigateTo: (path: string) => void;
  resourceCentreEmployees: Employee[];
  isEmployeeOnlyTab: boolean;
}

function downloadEmployeeUpload() {
  window.open(
    "https://drive.google.com/file/d/1ZgqkpAodcEc9nZ4dRwZobAPZ4hefSnK4/view?usp=sharing",
    "_blank"
  );
}

const Admins: React.FC<AdminsProps> = ({
  resourceCentre,
  editMode,
  createMode,
  employeeId,
  resourceCentreState,
  user,
  onRowClick,
  closeInfo,
  onClose,
  onSave,
  goBack: goBackAction,
  navigateTo,
  resourceCentreEmployees,
  isEmployeeOnlyTab = false
}) => {
  const dispatch = useDispatch();
  const isAccountSubscribed = useIsAccountSubscribed();
  const employeeLedger = ledgerFindBySubLedgerType(SubLedgerTypes.EMPLOYEE);

  let resourceCentreEmployee = {
    resourceCentreId: resourceCentre.id,
    departmentId: ALL_DEPARTMENT,
    ...(isAccountSubscribed && employeeLedger && createMode ? { ledgerId: employeeLedger.id } : {})
  } as Employee;

  if (editMode) {
    const foundRcEmployee = resourceCentreEmployees.find((rce) => rce.id === Number(employeeId));

    if (foundRcEmployee) {
      const normalized = {
        ...foundRcEmployee,
        userGroup: foundRcEmployee.userAuthenticable?.[0]?.userGroups?.[0]?.name || "",
        departmentId: foundRcEmployee?.departmentId || ALL_DEPARTMENT
      } as Employee;

      resourceCentreEmployee = !isAccountSubscribed
        ? (omit(normalized, [
            "openingBalance",
            "openingBalanceDate",
            "openingBalance",
            "ledgerId"
          ]) as Employee)
        : normalized;
    }
  }

  const { selectedEmployee } = resourceCentreState;
  const showCreateButton = useCan("clinic:createAdmin", { superAdminPass: true });
  const { twoFactorAuthentication } = resourceCentre?.subscriptions?.features || {};
  const departmentSubscription = resourceCentre?.subscriptions.features.department?.subscribed;
  const departments = useGetDepartments({
    showDeletedDepartments: false,
    resourceCentreId: resourceCentre.id
  });

  if (!resourceCentre) return null;
  return (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ height: "100%", minWidth: "600px" }}>
        <List
          automation="employeeList"
          title={`${t("employee.employees")} (${resourceCentreEmployees?.length || 0})`}
          rowHeight={50}
          createLabel={
            isEmployeeOnlyTab
              ? tl("employee.createEmployeeOnly")
              : tl("employee.administrativeUser")
          }
          defaultSortColumn="name"
          hideCreateButton={!showCreateButton}
          columns={[
            {
              key: "name",
              label: tl("employee.name"),
              sortable: true,
              sortFunction: (a, b) =>
                `${a.firstName}${a.lastName}`.toLowerCase() >
                `${b.firstName}${b.lastName}`.toLowerCase()
                  ? 1
                  : -1,
              formatter: (emp) => (
                <Typography variant="body2">
                  {employeeFullNameSelector({ firstName: emp.firstName, lastName: emp.lastName })}
                </Typography>
              )
            },
            departmentSubscription
              ? {
                  key: "department",
                  label: tl("department"),
                  formatter: (emp) => (
                    <Typography variant="body2">
                      {departments.find((dept) => dept.id === emp?.departmentId)?.name || ""}
                    </Typography>
                  )
                }
              : {
                  key: "",
                  label: ""
                },
            { key: "phone", label: tl("employee.phone") },
            { key: "email", label: tl("employee.email") },
            ...(twoFactorAuthentication?.subscribed
              ? [
                  {
                    key: "twoFactorAuthentication",
                    label: "2FA",
                    formatter: (row) => <UserChip allowed={row?.user?.enforcedTwoFactorAuth} />
                  }
                ]
              : [])
          ]}
          activeRow={selectedEmployee}
          data={resourceCentreEmployees}
          onRowClick={({ id }) => {
            onRowClick(Number(id));
          }}
          testmationLabel="employeeCreateAdmin"
          onCreateNew={
            Authorize(user).can("admin", "Employee")
              ? () =>
                  navigateTo(
                    `/resourcecentres/${resourceCentre.id}/${
                      isEmployeeOnlyTab
                        ? SETTING_ROUTES.NON_USER_EMPLOYEE
                        : SETTING_ROUTES.EMPLOYEES
                    }/create`
                  )
              : undefined
          }
        >
          <ListActions>
            {() => (
              <Menu>
                <CsvUploader
                  buttonText="Upload employees (.csv)"
                  columns={employeeUploadColumns}
                  requiredFieldInfoText="First name, Last name, Phone and email are mandatory for employee upload."
                  createDataApi={
                    isEmployeeOnlyTab ? batchUploadNonUserEmployee : batchUploadEmployee
                  }
                  useBatchUpload
                  runAfterSave={() =>
                    dispatch(
                      resourceCentreEmployeeActions.getResourceCentreEmployees({
                        resourceCentreId: resourceCentre.id
                      })
                    )
                  }
                  renderAdditionalInfo={() => (
                    <Typography
                      style={{
                        cursor: "pointer",
                        position: "fixed",
                        right: 20
                      }}
                    >
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <Link onClick={downloadEmployeeUpload}>{tl("stock.viewSample")}</Link>
                    </Typography>
                  )}
                />
                <MenuItem onClick={downloadEmployeeUpload}>{tl("stock.viewSample")}</MenuItem>
              </Menu>
            )}
          </ListActions>
        </List>
      </Box>

      {selectedEmployee && (
        <EmployeeInfo
          isEmployeeOnlyTab={isEmployeeOnlyTab}
          employeeId={selectedEmployee}
          handleClose={() => {
            navigateTo(
              `/resourcecentres/${resourceCentre.id}/${
                isEmployeeOnlyTab ? SETTING_ROUTES.NON_USER_EMPLOYEE : SETTING_ROUTES.EMPLOYEES
              }`
            );
            closeInfo();
          }}
        />
      )}

      {(createMode || editMode) && resourceCentreEmployee && (
        <EmployeeCreateEdit
          createMode={createMode}
          onClose={() => {
            onClose();
            goBackAction();
          }}
          employee={resourceCentreEmployee}
          isEmployeeOnlyTab={isEmployeeOnlyTab}
          departmentSubscription={Boolean(departmentSubscription)}
          onSave={(formData) => {
            onSave(
              {
                ...formData,
                ...(editMode ? { id: resourceCentreEmployee.id } : {}),
                ...(isEmployeeOnlyTab && { nonUserEmployee: true })
              } as any,
              resourceCentre.id,
              (id) => onRowClick(id)
            );
          }}
        />
      )}
    </Box>
  );
};

export default connect(
  (state: RootState) => ({
    permissionGroup: state.userContext?.userCreds?.userGroups[0],
    policies: state.userContext.policies || {}
  }),
  (dispatch: IThunkDispatch) => ({
    actions: bindActionCreators({ ...resourceCentreEmployeeActions }, dispatch),
    onSave: async (data, rcId, afterSave) => {
      dispatch(async (dispatchI, getState) => {
        if (data.id) {
          dispatchI(resourceCentreEmployeeActions.putResourceCentreEmployee(data));
          dispatchI(
            push(
              `/resourcecentres/${rcId}/${
                data?.nonUserEmployee ? SETTING_ROUTES.NON_USER_EMPLOYEE : SETTING_ROUTES.EMPLOYEES
              }`
            )
          );
        } else {
          await dispatchI(
            resourceCentreEmployeeActions.postResourceCentreEmployee(data, (employee: Employee) =>
              afterSave(employee.id)
            )
          );
          const currentState = getState();
          const hasError = currentState.error.find(
            (d) =>
              d &&
              typeof d !== "string" &&
              d.key === "POST_RESOURCECENTREEMPLOYEE" &&
              d.data.type === "UniqueViolation"
          );

          const userLimitError = currentState.error.find(
            ({ key, status }) => key === "POST_RESOURCECENTREEMPLOYEE" && status === 400
          );

          if (userLimitError) {
            dispatchI(
              NotificationActions.notificationAdd({
                id: new Date().getUTCMilliseconds(),
                variant: "error",
                message: tl("userLimitReached"),
                autoTimeout: true
              })
            );
            dispatchI(removeGeneralErrorByKey("POST_RESOURCECENTRESERVICEPROVIDER"));
          } else if (hasError) {
            dispatchI(
              NotificationActions.notificationAdd({
                id: new Date().getUTCMilliseconds(),
                variant: "error",
                message: tl("phoneNumberAlreadyExists"),
                autoTimeout: true
              })
            );
            dispatchI(removeGeneralErrorByKey("POST_RESOURCECENTREEMPLOYEE"));
          } else {
            dispatchI(
              NotificationActions.notificationAdd({
                id: new Date().getUTCMilliseconds(),
                variant: "success",
                message: tl("successAdminCreation"),
                autoTimeout: true
              })
            );
            dispatchI(
              push(
                `/resourcecentres/${rcId}/${
                  data?.nonUserEmployee
                    ? SETTING_ROUTES.NON_USER_EMPLOYEE
                    : SETTING_ROUTES.EMPLOYEES
                }`
              )
            );
            dispatch(
              resourceCentreEmployeeActions.getResourceCentreEmployees({ resourceCentreId: rcId })
            );
          }
        }
      });
    },
    navigateTo: (url: string) => {
      dispatch(push(url));
    },
    goBack: () => dispatch(goBack())
  })
)(Admins);
